import { ref } from 'vue';
import type { Ref } from 'vue';
import type { FeedCard, KdsRoundTableSeatPersona } from './types';

const DEFAULT_MAX_CARDS = 4;
const DEFAULT_BUFFER_SIZE = 8;
const MAX_DRAW_RETRIES = 30;

/**
 * Composable managing the animated quote feed for KdsRoundTable.
 *
 * Maintains a reactive list of up to `maxCards` FeedCard entries.
 * Uses a circular buffer of `bufferSize` indices to avoid quote repetition
 * in the last N draws. Falls back to a deterministic pick after 30 retries.
 *
 * @param personas - Array of seat personas (index matches seat index).
 * @param quotes   - Readonly array of quote strings to draw from.
 * @param options  - Optional overrides for maxCards and bufferSize.
 * @returns feedCards reactive list, pushSpeaker function, reset function.
 */
export const useQuoteFeed = (
  personas: KdsRoundTableSeatPersona[],
  quotes: readonly string[],
  options?: { maxCards?: number; bufferSize?: number }
): {
  feedCards: Ref<FeedCard[]>;
  pushSpeaker: (seatIndex: number, opts?: { isInitial?: boolean }) => void;
  reset: () => void;
} => {
  const maxCards = options?.maxCards ?? DEFAULT_MAX_CARDS;
  const bufferSize = options?.bufferSize ?? DEFAULT_BUFFER_SIZE;

  const feedCards = ref<FeedCard[]>([]);
  // Circular buffer tracking the indices of recently used quotes.
  const recentBuffer: number[] = [];
  let turnIdCounter = 0;

  /**
   * Pick a quote index not present in the recent buffer.
   * Retries up to MAX_DRAW_RETRIES times with Math.random,
   * then falls back to the first index not in the buffer (deterministic).
   */
  const pickQuoteIndex = (): number => {
    const total = quotes.length;

    for (let attempt = 0; attempt < MAX_DRAW_RETRIES; attempt++) {
      const candidate = Math.floor(Math.random() * total);
      if (!recentBuffer.includes(candidate)) {
        return candidate;
      }
    }

    // Deterministic fallback: first index not in the buffer.
    for (let i = 0; i < total; i++) {
      if (!recentBuffer.includes(i)) {
        return i;
      }
    }

    // All quotes are in the buffer (impossible with 20 quotes + buffer 8), return index 0.
    return 0;
  };

  /**
   * Record a quote index in the circular buffer, evicting the oldest entry
   * when the buffer reaches its maximum size.
   */
  const recordInBuffer = (quoteIndex: number): void => {
    recentBuffer.push(quoteIndex);
    if (recentBuffer.length > bufferSize) {
      recentBuffer.shift();
    }
  };

  /**
   * Insert a new FeedCard at the head of feedCards for the given seat index.
   * Removes the oldest card if the list exceeds maxCards.
   *
   * @param seatIndex  - 0-based index of the speaking seat (maps to personas array).
   * @param opts       - Optional flags, e.g. `isInitial` to skip enter animation.
   */
  const pushSpeaker = (seatIndex: number, opts?: { isInitial?: boolean }): void => {
    const persona = personas[seatIndex] ?? personas[0]!;
    const quoteIndex = pickQuoteIndex();
    recordInBuffer(quoteIndex);
    const quote = quotes[quoteIndex] ?? quotes[0]!;

    const card: FeedCard = {
      turnId: ++turnIdCounter,
      personaName: persona.name,
      personaId: persona.personaId,
      segment: persona.segment,
      quote,
      isInitial: opts?.isInitial ?? false
    };

    // Insert at head; trim to maxCards.
    const next = [card, ...feedCards.value];
    feedCards.value = next.slice(0, maxCards);
  };

  /**
   * Clear the feed and reset the buffer and turn counter.
   */
  const reset = (): void => {
    feedCards.value = [];
    recentBuffer.length = 0;
    turnIdCounter = 0;
  };

  return { feedCards, pushSpeaker, reset };
};
